// ============================================================================
// StudentGuard - Route protection for student-specific states
// ============================================================================
//
// This guard runs AFTER ProtectedRoute (authentication check).
// It checks student-specific conditions and redirects to appropriate pages.
//
// Usage in App.tsx or student routes:
//   <ProtectedRoute>
//     <StudentGuard>
//       <StudentRoutes />
//     </StudentGuard>
//   </ProtectedRoute>
//
// ============================================================================

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { StudentState, STUDENT_STATE_ROUTES } from "../types/StudentModel";
import { deriveStudentState, StudentProfile } from "../utils/studentUtils";
import FullScreenLoading from "../components/FullScreenLoading";

interface StudentGuardProps {
  children: React.ReactElement;
}

/**
 * Error/state routes — accessible by non-active students, but active students
 * get redirected away from these back to the classroom.
 */
const ERROR_PAGE_ROUTES = [
  "/student/account-inactive",
  "/student/waiting-assignment",
  "/student/contact-teacher",
  "/student/placement-test",
];

/**
 * Routes accessible by ALL students regardless of state (no redirect).
 */
const UNIVERSAL_ROUTES = [
  "/student/profile",
];

const isErrorPageRoute = (pathname: string): boolean => {
  return ERROR_PAGE_ROUTES.some((route) => pathname.startsWith(route));
};

const isUniversalRoute = (pathname: string): boolean => {
  return UNIVERSAL_ROUTES.some((route) => pathname.startsWith(route));
};

/**
 * StudentGuard component
 *
 * Checks student state and redirects to appropriate page:
 * - INACTIVE → /student/account-inactive
 * - UNASSIGNED → /student/waiting-assignment
 * - INVALID_PROGRAM → /student/contact-teacher
 * - ACTIVE → allows access to requested route
 */
export const StudentGuard: React.FC<StudentGuardProps> = ({ children }) => {
  const { user, isAuthLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to complete
  if (isAuthLoading) {
    return <FullScreenLoading />;
  }

  // Cast user to StudentProfile (it contains the student fields)
  const profile = user as StudentProfile | null;

  // Derive student state
  const studentState = deriveStudentState(profile, isAuthLoading);

  // Universal routes are always accessible regardless of state
  if (isUniversalRoute(location.pathname)) {
    return children;
  }

  // Error page routes: allow non-active students, redirect active students to classroom
  if (isErrorPageRoute(location.pathname)) {
    if (studentState.state === StudentState.ACTIVE) {
      return <Navigate to="/student/my-classroom" replace />;
    }
    return children;
  }

  // Check state and redirect if needed
  switch (studentState.state) {
    case StudentState.INACTIVE:
      return (
        <Navigate
          to={STUDENT_STATE_ROUTES[StudentState.INACTIVE]}
          state={{ from: location }}
          replace
        />
      );

    case StudentState.UNASSIGNED:
      return (
        <Navigate
          to={STUDENT_STATE_ROUTES[StudentState.UNASSIGNED]}
          state={{ from: location }}
          replace
        />
      );

    case StudentState.INVALID_PROGRAM:
      return (
        <Navigate
          to={STUDENT_STATE_ROUTES[StudentState.INVALID_PROGRAM]}
          state={{ from: location }}
          replace
        />
      );

    case StudentState.ACTIVE:
      // Student is fully active, allow access
      return children;

    default:
      // Fallback - shouldn't happen
      console.error(
        "[StudentGuard] Unknown student state:",
        studentState.state
      );
      return (
        <Navigate to={STUDENT_STATE_ROUTES[StudentState.UNASSIGNED]} replace />
      );
  }
};

export default StudentGuard;
